import { SelectionPreviewChannel } from 'libs/weighted/src/lib/components/weighted-model-forecast-charts/model-forecast-selection-common/selection-preview.types';
import {
  ModelConfig,
  ModelParameter,
  ModelView,
  WidgetModelFilterConfig,
} from '@firebird-web/model-store';
import { RenewablesProducts } from 'libs/renewable/src/lib/models/renewables.models';
import {
  EnsembleSpread,
  WeightedForecastGraph,
} from 'libs/weighted/src/lib/interfaces/weighted-model-forecast.interface';
import { RunDatesEntity } from '@firebird-web/run-dates-store';
import {
  ChartAbsoluteSelectionState,
  ChartBaseline,
  ChartLegendsRelativeState,
  LegendDock,
} from 'libs/weighted-store/src/lib/model';
import { ILoadWidgetLegend, SubZone } from '../interfaces';
import { LocationPickerCustomList } from '@firebird-web/location-picker-store';
import { RangeOptions } from 'libs/weighted/src/lib/constants/relative-graph.constants';
import { ExtendedRangeOptions } from '@firebird-web/weighted';
import { DifferencesSelection } from 'libs/weighted/src/lib/components/model-forecast-differences-selection/model-forecast-differences-selection.types';

export enum WidgetTypes {
  heatMap = 'heat-map',
  interactiveMap = 'interactive-map',
  weatherTimeline = 'weather-timeline',
  compMatrix = 'comp-matrix',
  renewables = 'renewables',
  naRenewables = 'na-renewables',
  model = 'model',
  weightedGraph = 'weighted-graph',
  weightedGraphDifferences = 'weighted-graph-differences',
  analysis = 'analysis',
  weighted = 'weighted',
  demand = 'demand',
  blurb = 'blurb',
  periodMapsHeadlines = 'period-maps-headlines',
  ec46WeightedDDGraph = 'ec46-weighted-dd-graph',
  blog = 'blog',
  // Saved in every dashboard's layout JSON; never rename.
  teleconnection = 'teleconnection',
}

export type TeleconnectionWidgetView =
  | 'individual'
  | 'modelComparison'
  | 'trends';

// The Teleconnections page's selections, kept per widget instead of per route.
export interface ITeleconnectionWidgetConfig {
  teleconnectionId: string;
  view: TeleconnectionWidgetView;
  // ECMWF | AIFS | GFS | GEM; used by the individual and trends views.
  model: string;
  // OP | ENS; used by the trends view.
  modelType: string;
  // Observation history in days: 7 or 30.
  range: number;
  // Legend toggles, keyed like the page's (createSeriesVisibilityKey).
  seriesVisibility?: Record<string, Record<string, boolean>>;
}

export interface ISummaryWidgetConfig {
  compareRunDate: string;
  compareToDate?: { initTime: string; type: string };
  continent: string;
  continentId: string;
  primaryLensId: string;
  customListId: string;
  customList: any;
  displayName: string;
  forecastType: string;
  duration: string;
  filter: string;
  listName: string;
  precipUnit: string;
  primaryLens: string;
  region: string;
  dailyPeriod: string;
  runDate: string;
  runDates: RunDatesEntity[];
  storageTime: string;
  secondaryLens: string;
  size: number;
  tempUnit: string;
  type: WidgetTypes;
  isNew?: boolean;
  layers?: string[];
  mapLayers?: string[];
  showRoadsAndPlaces: boolean;
  loadWidgetLegends: ILoadWidgetLegend[];
  siteId: string;
  widgetId: string;
  windUnit: string;
  currentDate: string;
  city: string;
  showDifference: boolean;
  differencesSelection?: DifferencesSelection;
  chartLegends: any;
  showDecimal: boolean;
  showNormal: boolean;
  showMarketFirst: boolean;
  biasCorrected: boolean;
  modelForecast: string;
  runTime: string;
  modelRun: string;
  subzone: SubZone;
  shouldReload: boolean;
  isUpdateEngine?: boolean;
  scale?: string;
  legend?: Record<string, boolean>;
  poolId?: string;
  pool?: string;
  h?: number;
  isAggregate?: boolean;
  product?: RenewablesProducts;
  selectedContinents: string[];
  activeRange?: ExtendedRangeOptions;
  range?: {
    label: string;
    value: {
      min: number;
      max: number;
    };
  };
}

export interface IWidgetLocationState {
  type: string;
  continent: string;
  region?: string;
  siteId?: string;
  widgetId?: string;
  modelConfig?: IWidgetModelConfig;
  customList?: LocationPickerCustomList;
  displayName?: string;
}

export interface IWidgetModelConfig {
  model: WidgetModelFilterConfig;
  parameter: ModelParameter;
  run: { id: string; title: string };
  view: ModelView | { id: string; title: string };
  h?: number;
  w?: number;
  selectedCycle?: { id: string; title: string; diff?: number };
  resolvedRun?: string;
}

export type WidgetTypeList =
  | WidgetTypes.heatMap
  | WidgetTypes.renewables
  | WidgetTypes.analysis
  | WidgetTypes.weightedGraph
  | WidgetTypes.weightedGraphDifferences
  | WidgetTypes.weighted
  | WidgetTypes.ec46WeightedDDGraph
  | WidgetTypes.model
  | WidgetTypes.demand
  | WidgetTypes.blurb
  | WidgetTypes.periodMapsHeadlines
  | WidgetTypes.blog
  | WidgetTypes.teleconnection;

export type WidgetConfigType =
  | ISummaryWidgetConfig
  | IWeightedModelGraphWidgetConfig;

export interface IWeightedModelGraphWidgetConfig {
  ensSpread: { label: string; value: string };
  ensembleSpreads: EnsembleSpread[];
  continent: string;

  chartData: WeightedForecastGraph;
  showBiasCorrected: boolean;
  siteId: string;
  size: number;
  modelWeightedForecast: string;
  type: string;
}

export interface IWeightedModelGraphDifferencesWidgetConfig {
  chartSpread: { labelKey: string; value: string };
  chartSpreadAbsolute?: { labelKey: string; value: string };
  ensembleSpreads: EnsembleSpread[];
  continent: string;
  selectedRange: RangeOptions;

  chartData: WeightedForecastGraph;
  showBiasCorrected: boolean;
  showFridayRuns: boolean;
  siteId: string;
  size: number;
  modelWeightedForecast: string;
  type: string;
  runSelections: any;
  differenceConfig: any[];
  modelRunsSelectionView?: string;
  relativeSelectionState?: ChartLegendsRelativeState | null;
  absoluteSelectionState?: ChartAbsoluteSelectionState | null;
  relativeBaseline?: ChartBaseline | null;
  absoluteBaseline?: ChartBaseline | null;
  legendDock?: LegendDock;
  legendWidth?: number | null;
}

export interface IWidgetSummaryPanelData {
  widgetType: WidgetTypeList;
  widgetConfig: ISummaryWidgetConfig;
  widgetId: string;
  isNew: boolean;
  widgetTitle?: string;
}
export interface IWidgetLocationData {
  continent: string;
  region: string;
  name: string;
  siteId: string;
  displayName: string;
  customList?: LocationPickerCustomList;
}

export interface IWidgetWeightedLocationData {
  regionName: string;
  regionId: string;
  continentId: string;
}

export interface IWeightedGraphPanelData {
  widgetType: WidgetTypes.weightedGraph;
  widgetConfig: any; //to be changed
  widgetId: string;
  isNew: boolean;
}

export interface IWeightedGraphDifferencesPanelData {
  widgetType: WidgetTypes.weightedGraphDifferences;
  widgetConfig: any; //to be changed
  widgetId: string;
  isNew: boolean;
  /** Live selection preview back to the open widget; absent for new widgets. */
  previewChannel?: SelectionPreviewChannel;
}

export interface ISingleModelWidgetData {
  widgetType: WidgetTypes.model;
  widgetConfig: ModelConfig; // needs to be changed
  widgetId: string;
  isNew: boolean;
}
export interface ISumTableConfigOptions {
  text: string;
  id: string;
}
export interface IImapDefault {
  isNew: boolean;
  layers: string[];
  showRoadsAndPlaces: boolean;
  mapLayers: string[];
}
